import React, { useCallback, useEffect, useState } from "react"
import {
    MdDeleteOutline,
    MdFilterAlt,
    MdHistory,
    MdTune,
} from "react-icons/md"
import { toast } from "react-toastify"
import api from "../../services/api"
import BulkActionHistory from "./BulkActionHistory"

const formatDuration = seconds => {
    const total = Math.max(0, Number(seconds) || 0)
    if (total < 60) return `${total}s`
    const minutes = Math.floor(total / 60)
    if (minutes < 60) return `${minutes}min ${total % 60}s`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ${minutes % 60}min`
}

const ManagementLogCrm = () => {
    const [data, setData] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    const getAll = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setIsLoading(true)
            const response = await api.get("/leads/management")
            setData(response.data?.data || [])
        } catch (err) {
            if (!silent) toast.error(err?.response?.data?.message || "Não foi possível carregar o histórico.")
        } finally {
            if (!silent) setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        getAll()
    }, [getAll])

    const handleCancel = async id => {
        try {
            setIsLoading(true)
            const response = await api.patch(`/leads/management/${id}/cancel`)
            const status = response?.data?.data?.status || "canceled"
            setData(previous => previous.map(item => item._id === id ? { ...item, status, can_cancel: false } : item))
            toast.success(response?.data?.message)
            return true
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível cancelar a execução.")
            return false
        } finally {
            setIsLoading(false)
        }
    }

    const getDetails = item => {
        const details = [
            { label: "Registros previstos", value: item.count },
            { label: "Processados / restantes", value: `${item.count_updated} / ${item.remaining}` },
            { label: "Falhas", value: item.count_failed || 0 },
            { label: "Início", value: item.started_at || "Ainda não iniciado" },
            { label: "Conclusão", value: item.finished_at || "Em aberto" },
            { label: "Duração registrada", value: formatDuration(item.duration_seconds) },
            { label: "Tomadas pelo worker", value: item.attempts || 0 },
            { label: "Última atualização", value: item.updated_at },
            { label: "Identificador", value: item._id, executionId: true },
        ]
        if (item.changes?.length) details.push({ label: "Alterações solicitadas", items: item.changes })
        if (item.filter_summary?.length) details.push({ label: "Filtros utilizados", items: item.filter_summary })
        if (item.error_samples?.length) details.push({
            label: "Amostras de falha",
            items: item.error_samples.map(error => `${error.lead_id}: ${error.message}`),
        })
        return details
    }

    return (
        <BulkActionHistory
            data={data}
            description="Acompanhe alterações e exclusões de leads, progresso, responsável e eventuais falhas."
            isLoading={isLoading}
            onRefresh={getAll}
            onCancel={handleCancel}
            cancelText="Se ela já tiver começado, será interrompida com segurança ao final do lote atual."
            progressLabel="Leads processados"
            getActionIcon={item => item.action === "delete" ? MdDeleteOutline : MdTune}
            getMetadata={item => [
                { icon: MdFilterAlt, value: item.source_label },
                { icon: MdHistory, value: formatDuration(item.duration_seconds) },
            ]}
            getDetails={getDetails}
            getError={item => item.error || (item.count_failed > 0
                ? `${item.count_failed} lead${item.count_failed === 1 ? " apresentou" : "s apresentaram"} falha durante o processamento.`
                : null
            )}
        />
    )
}

export default ManagementLogCrm
